import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const VISITS_KEY = 'mtb-visit-count';
const DISMISSED_KEY = 'pwa-banner-dismissed';

/** Pages de conversion : l'invitation d'installation n'y est jamais affichée. */
const EXCLUDED_PATHS = ['/quote-request', '/contact', '/appointment'];

/** Compte les visites (une par session) pour ne proposer l'installation qu'à partir de la 2ᵉ. */
const readVisitCount = (): number => {
  try {
    const stored = Number(localStorage.getItem(VISITS_KEY) ?? '0');
    if (!sessionStorage.getItem('mtb-visit-counted')) {
      const next = stored + 1;
      localStorage.setItem(VISITS_KEY, String(next));
      sessionStorage.setItem('mtb-visit-counted', '1');
      return next;
    }
    return stored;
  } catch {
    return 1;
  }
};

const InstallPWAButton = () => {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [eligible, setEligible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    try {
      if (localStorage.getItem(DISMISSED_KEY)) {
        setDismissed(true);
        return;
      }
    } catch { /* ignore */ }

    const visits = readVisitCount();
    // Éligible dès la 2ᵉ visite, ou après une interaction significative
    // (défilement profond ou temps de lecture) lors de la première visite.
    if (visits >= 2) {
      setEligible(true);
    } else {
      const markEligible = () => setEligible(true);
      const onScroll = () => {
        const scrolled = window.scrollY / Math.max(document.body.scrollHeight - window.innerHeight, 1);
        if (scrolled > 0.5) markEligible();
      };
      const timer = window.setTimeout(markEligible, 60_000);
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => {
        window.clearTimeout(timer);
        window.removeEventListener('scroll', onScroll);
      };
    }
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const installed = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setIsInstalled(true);
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISSED_KEY, Date.now().toString()); } catch { /* ignore */ }
  };

  const onExcludedPage = EXCLUDED_PATHS.some((p) => pathname.startsWith(p));

  if (isInstalled || dismissed || !eligible || !deferredPrompt || onExcludedPage) return null;

  return (
    <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+72px)] left-4 right-4 z-40 md:bottom-4 md:left-auto md:right-4 md:max-w-sm animate-slide-up">
      <div className="bg-card border border-border rounded-xl shadow-lg p-4 flex items-start gap-3">
        <div className="p-2 bg-accent/10 rounded-lg shrink-0">
          <Download className="w-5 h-5 text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-semibold text-sm text-foreground">
            {t('pwa.install.title')}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('pwa.install.desc')}
          </p>
          <Button
            size="sm"
            onClick={handleInstall}
            className="mt-2 bg-accent text-accent-foreground hover:bg-mtb-gold-dark text-xs h-8"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />
            {t('pwa.install.button')}
          </Button>
        </div>
        <button
          onClick={handleDismiss}
          className="p-1 text-muted-foreground hover:text-foreground transition-colors shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
          aria-label={t('pwa.install.close')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallPWAButton;
